import React, { ReactNode } from 'react';
import { ShiftUIProvider, useShiftUI } from './ShiftUIProvider';
import { ShiftDataProvider, useShiftData, useShiftDataSafe } from './ShiftDataProvider';
import { InfiniteScrollShiftDataProvider, useInfiniteScrollShiftData, useInfiniteScrollShiftDataSafe, useIsInfiniteScrollMode } from './InfiniteScrollShiftDataProvider';
import { ShiftHoverProvider } from './ShiftHoverProvider';
import { TooltipProvider } from '@/components/ui/tooltip';

/** Calendar scroll mode - monthly (traditional) or infinite scroll */
export type CalendarScrollMode = 'monthly' | 'infinite';

// Composed Provider that maintains the same interface as UnifiedShiftProvider
interface ComposedShiftProviderProps {
  children: ReactNode;
  businessId: string | null;
  initialMonth?: Date;
  onSelectedDateChange?: (date: Date | null) => void;
  /** Calendar scroll mode - defaults to 'monthly' for backward compatibility */
  scrollMode?: CalendarScrollMode;
  /** Whether infinite scroll is enabled (alternative to scrollMode prop) */
  infiniteScrollEnabled?: boolean;
}

export const ComposedShiftProvider: React.FC<ComposedShiftProviderProps> = ({
  children,
  businessId,
  initialMonth = new Date(),
  onSelectedDateChange,
  scrollMode: scrollModeProp,
  infiniteScrollEnabled = false
}) => {
  // Determine effective scroll mode
  const effectiveScrollMode: CalendarScrollMode = scrollModeProp ?? (infiniteScrollEnabled ? 'infinite' : 'monthly');

  return (
    // TooltipProvider at root level - all tooltips share this single provider
    // This eliminates 10+ redundant TooltipProvider instances across components
    <TooltipProvider>
      {/* ShiftHoverProvider is outermost - isolated to prevent re-render cascades */}
      {/* Only TimeHeader subscribes to hover state changes */}
      <ShiftHoverProvider>
        <ShiftUIProvider
          businessId={businessId}
          initialMonth={initialMonth}
          onSelectedDateChange={onSelectedDateChange}
          scrollMode={effectiveScrollMode}
        >
          {effectiveScrollMode === 'infinite' ? (
            <InfiniteScrollDataProviderWrapper businessId={businessId}>
              {children}
            </InfiniteScrollDataProviderWrapper>
          ) : (
            <ShiftDataProviderWrapper businessId={businessId}>
              {children}
            </ShiftDataProviderWrapper>
          )}
        </ShiftUIProvider>
      </ShiftHoverProvider>
    </TooltipProvider>
  );
};

// Wrapper to access currentDisplayMonth from UI context (for monthly mode)
const ShiftDataProviderWrapper: React.FC<{ children: ReactNode; businessId: string | null }> = ({
  children,
  businessId
}) => {
  const { currentDisplayMonth } = useShiftUI();

  return (
    <ShiftDataProvider businessId={businessId} currentDisplayMonth={currentDisplayMonth}>
      {children}
    </ShiftDataProvider>
  );
};

// Wrapper for infinite scroll mode - uses currentScrollDate for reference
const InfiniteScrollDataProviderWrapper: React.FC<{ children: ReactNode; businessId: string | null }> = ({
  children,
  businessId
}) => {
  const { currentScrollDate } = useShiftUI();

  return (
    <InfiniteScrollShiftDataProvider
      businessId={businessId}
      referenceDate={currentScrollDate || new Date()}
    >
      {children}
    </InfiniteScrollShiftDataProvider>
  );
};

/**
 * Hook to get data context - works in both monthly and infinite scroll modes
 * Returns the appropriate data context based on which provider is active
 */
const useDataContext = () => {
  const isInfiniteMode = useIsInfiniteScrollMode();

  // In infinite scroll mode, use InfiniteScrollShiftData
  // In monthly mode, use ShiftData
  // We need to call both hooks but only one will be in a valid provider
  const infiniteScrollContext = useInfiniteScrollShiftDataSafe();
  const shiftDataContext = useShiftDataSafe();

  if (isInfiniteMode && infiniteScrollContext) {
    return infiniteScrollContext;
  }

  if (!isInfiniteMode && shiftDataContext) {
    return shiftDataContext;
  }

  // Fallback - should not happen if providers are set up correctly
  throw new Error('No data provider found. Make sure you are within ComposedShiftProvider.');
};

// Combined hooks that merge UI and Data contexts for backward compatibility
export const useShiftManagement = () => {
  const uiContext = useShiftUI();
  const dataContext = useDataContext();

  return {
    // UI state and actions
    currentDisplayMonth: uiContext.currentDisplayMonth,
    selectedDate: uiContext.selectedDate,
    calendarKey: uiContext.calendarKey,
    hidePastDays: uiContext.hidePastDays,
    expandAllDays: uiContext.expandAllDays,
    show24Hours: uiContext.show24Hours,
    showEvents: uiContext.showEvents,
    hourCellWidthPx: uiContext.hourCellWidthPx,
    isActionsPanelOpen: uiContext.isActionsPanelOpen,
    unifiedActionsPanelMode: uiContext.unifiedActionsPanelMode,
    refreshKey: uiContext.refreshKey,

    // Calendar actions
    setCurrentDisplayMonth: uiContext.setCurrentDisplayMonth,
    handleNextMonth: uiContext.handleNextMonth,
    handlePreviousMonth: uiContext.handlePreviousMonth,
    handleSetToday: uiContext.handleSetToday,
    handleMonthSelect: uiContext.handleMonthSelect,

    // Day selection
    setSelectedDate: uiContext.setSelectedDate,
    handleDaySelect: uiContext.handleDaySelect,
    handleCloseDayPanel: uiContext.handleCloseDayPanel,

    // UI settings
    setHidePastDays: uiContext.setHidePastDays,
    handleToggleHidePastDays: uiContext.handleToggleHidePastDays,
    setExpandAllDays: uiContext.setExpandAllDays,
    handleToggleExpandAllDays: uiContext.handleToggleExpandAllDays,
    setShow24Hours: uiContext.setShow24Hours,
    setShowEvents: uiContext.setShowEvents,
    setHourCellWidthPx: uiContext.setHourCellWidthPx,
    setIsActionsPanelOpen: uiContext.setIsActionsPanelOpen,
    setUnifiedActionsPanelMode: uiContext.setUnifiedActionsPanelMode,

    // Selection
    selectedShiftIds: uiContext.selectedShiftIds,
    hasShiftSelection: uiContext.hasShiftSelection,
    selectionCount: uiContext.selectionCount,
    selectShift: uiContext.selectShift,
    selectAllShifts: uiContext.selectAllShifts,
    clearShiftSelection: uiContext.clearShiftSelection,
    toggleShift: uiContext.toggleShift,
    isShiftSelected: uiContext.isShiftSelected,

    // Real-time
    isRealtimeConnected: uiContext.isRealtimeConnected,
    activeUsers: uiContext.activeUsers,
    usersEditingShift: uiContext.usersEditingShift,
    setEditingShift: uiContext.setEditingShift,

    // Utility
    forceRefresh: uiContext.forceRefresh,

    // Data from data context
    businessId: dataContext.businessId,
    businessTimezone: dataContext.businessTimezone,
  };
};

export const useUnifiedShift = () => {
  const uiContext = useShiftUI();
  const dataContext = useDataContext();

  // Return combined context matching the original UnifiedShiftProvider interface
  return {
    // UI state
    ...uiContext,

    // Data state
    ...dataContext,

    // Create selectedShifts by filtering shifts with selected IDs
    selectedShifts: dataContext.shifts.filter(shift => uiContext.selectedShiftIds.has(shift.id)),

    // Override selectAllShifts to work with actual shifts data
    selectAllShifts: () => {
      const allShiftIds = new Set(dataContext.shifts.map(shift => shift.id));
      dataContext.shifts.forEach(shift => uiContext.selectShift(shift.id, true));
    },
  };
};

// Re-export individual hooks for granular access
export { useShiftUI } from './ShiftUIProvider';
export { useShiftData } from './ShiftDataProvider';
export { useInfiniteScrollShiftData, useInfiniteScrollShiftDataSafe, useIsInfiniteScrollMode } from './InfiniteScrollShiftDataProvider';
export { useShiftHover, useSetShiftHover } from './ShiftHoverProvider';
export type { CalendarScrollMode };